import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { addHours, parseISO, format } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useSWR from 'swr';
import api from '../services/api';
import { toBrazilMidnightIso } from '../util/utils';

const useAgenda = () => {
  const location = useLocation();
  const { funcionario } = useSelector(state => state.funcionario);
  const medico = useSelector(state => state.doctor.profile);

  // Inicializa lendo da URL ou usa médico padrão
  const getInitialMedico = () => {
    const searchParams = new URLSearchParams(location.search);
    const medFromUrl = searchParams.get('med');
    if (medFromUrl) {
      const medNum = Number(medFromUrl);
      if (!isNaN(medNum)) return medNum;
    }
    return medico?.codMed || 5; // médico padrão é o Dr Fabio
  };

  const getInitialDate = () => {
    const searchParams = new URLSearchParams(location.search);
    const dateFromUrl = searchParams.get('date');
    if (dateFromUrl) {
      const date = new Date(dateFromUrl);
      if (!isNaN(date.getTime())) {
        // A data na URL já tem subHours(3) aplicado, então adicionamos 3 horas de volta
        // para obter a data original, depois normalizamos para meia-noite local
        const dateWithHoursAdded = addHours(date, 3);
        const normalized = new Date(dateWithHoursAdded);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
      }
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const [idMedico, setIdMedico] = useState(getInitialMedico);
  const [dateAgenda, setDateAgenda] = useState(getInitialDate);
  const isInternalUpdateRef = useRef(false);
  const lastUrlParamsRef = useRef(location.search);

  // Lê parâmetros da URL quando a URL mudar externamente (não por atualização interna)
  useEffect(() => {
    const currentSearch = location.search;

    if (currentSearch === lastUrlParamsRef.current) return; // Se a URL não mudou, não faz nada

    // Se foi uma atualização interna, ignora e atualiza a ref
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      lastUrlParamsRef.current = currentSearch;
      return;
    }

    const searchParams = new URLSearchParams(currentSearch);
    const medFromUrl = searchParams.get('med');
    const dateFromUrl = searchParams.get('date');

    if (medFromUrl) {
      const medNum = Number(medFromUrl);
      if (!isNaN(medNum)) {
        setIdMedico(prev => {
          if (prev !== medNum) return medNum;
          return prev;
        });
      }
    }

    // Só atualiza a data se o parâmetro date estiver presente na URL
    // e for diferente da data atual
    if (dateFromUrl) {
      const dateFromUrlParsed = new Date(dateFromUrl);
      if (!isNaN(dateFromUrlParsed.getTime())) {
        // A data na URL já tem subHours(3) aplicado, então adicionamos 3 horas de volta
        // para obter a data original, depois normalizamos para meia-noite local
        const dateWithHoursAdded = addHours(dateFromUrlParsed, 3);
        const urlDateNormalized = new Date(dateWithHoursAdded);
        urlDateNormalized.setHours(0, 0, 0, 0);

        setDateAgenda(prev => {
          const currentDate = new Date(prev);
          currentDate.setHours(0, 0, 0, 0);

          // Compara apenas data, ignorando hora
          const datesAreDifferent =
            currentDate.getFullYear() !== urlDateNormalized.getFullYear() ||
            currentDate.getMonth() !== urlDateNormalized.getMonth() ||
            currentDate.getDate() !== urlDateNormalized.getDate();

          if (datesAreDifferent) return urlDateNormalized;

          return prev;
        });
      }
    }
    // Se date não estiver na URL, não faz nada (preserva a data atual)

    lastUrlParamsRef.current = currentSearch;
  }, [location.search]);

  // Meia-noite BRT — mesmo helper usado no faturamento (toBrazilMidnightIso)
  const dateAgendaFormatted = useMemo(
    () => toBrazilMidnightIso(dateAgenda),
    [dateAgenda]
  );

  const swrKey =
    idMedico && dateAgendaFormatted
      ? ['agendas', idMedico, dateAgendaFormatted]
      : null;

  const fetcherAgendas = useCallback(
    async key => {
      try {
        const [, idMed, dateFormatted] = key;
        const medicoId = idMed || (funcionario ? 5 : null);
        if (!medicoId) return [];

        const response = await api.get(
          `medico/agendas?id=${medicoId}&dt=${dateFormatted}`
        );

        const data = response?.data?.map(agenda => ({
          ...agenda,
          dataFormatada: format(parseISO(agenda.DATAAGENDA), 'dd/MM/yyyy', {
            locale: pt,
          }),
        }));

        return { data };
      } catch (error) {
        throw new Error(
          error?.response?.data?.error || 'Erro ao carregar agendas!'
        );
      }
    },
    [funcionario]
  );

  const { data, error, mutate } = useSWR(swrKey, fetcherAgendas, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    // dedupingInterval: 2000,
  });

  const markInternalUpdate = useCallback(() => {
    isInternalUpdateRef.current = true;
  }, []);

  return {
    agendamentos: data?.data || [],
    idMedico,
    setIdMedico,
    dateAgenda,
    setDateAgenda,
    dateAgendaFormatted,
    mutateAgendas: mutate,
    isLoading: !data && !error,
    markInternalUpdate,
  };
};

export default useAgenda;
